import axios from "axios";
import Cookies from "js-cookie";
import { forceLogout } from "store/slice/userSlice";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://staging-api.sartointelligence.com";

const api = axios.create({
  baseURL: BACKEND_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

let isRefreshing = false;
let isLoggingOut = false;
let failedQueue = [];

const processQueue = (error = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });

  failedQueue = [];
};

const forceLogoutUser = (store) => {
  if (isLoggingOut) return;

  isLoggingOut = true;

  Cookies.remove("accessToken");
  Cookies.remove("refreshToken");

  store.dispatch(forceLogout());

  window.location.replace("/");
};

export const setupInterceptors = (store) => {
  api.interceptors.response.use(
    (response) => response,

    async (error) => {
      const originalRequest = error.config;

      if (!originalRequest) {
        return Promise.reject(error);
      }

      const status = error.response?.status;
      const url = originalRequest.url || "";

      const isAuthEndpoint =
        url.includes("/api/v1/auth/login") ||
        url.includes("/api/v1/auth/verify") ||
        url.includes("/api/v1/auth/refresh");

      // Network error should usually NOT logout user.

      if (!error.response) {
        console.log("❌ Network error / no response");
        return Promise.reject(error);
      }

      // Only handle 401
      if (status !== 401) {
        return Promise.reject(error);
      }

      // If refresh itself fails with 401, logout
      if (url.includes("/api/v1/auth/refresh")) {
        console.log("❌ Refresh endpoint returned 401 — logging out");
        forceLogoutUser(store);
        return Promise.reject(error);
      }

      // Do not refresh login/verify APIs
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      // If the request was already retried and still got 401, logout.
      if (originalRequest._retry) {
        console.log("❌ Retried request still returned 401 — logging out");
        forceLogoutUser(store);
        return Promise.reject(error);
      }

      if (isLoggingOut) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // If refresh is already running then wait
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        console.log("🔄 Refreshing token...");

        await api.post("/api/v1/auth/refresh");

        console.log("✅ Token refreshed");

        isRefreshing = false;
        processQueue();

        return api(originalRequest);
      } catch (refreshError) {
        console.log("❌ Refresh failed — logging out");

        isRefreshing = false;
        processQueue(refreshError);

        forceLogoutUser(store);

        return Promise.reject(refreshError);
      }
    }
  );
};

export default api;
